import { NgTemplateOutlet } from '@angular/common';
import { Component, DOCUMENT, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DragAndDropDocument, ReportTemplate } from '../../../models';
import {
  DocumentPipelineService,
  DocumentViewerService,
  DragAndDropService,
  EmailService,
  FileService,
  ModalService,
  NotificationService,
  ReportAuditService,
} from '../../../services';
import { BaseComponent } from '../../base/base.component';
import { EmailModalComponent } from '../../email-modal/email-modal.component';
import { ReportStatuses } from '../../../constants';
import { ignoreModalConfirm, ignoreModalError } from '../../../helpers';
import { OkModalComponent } from '../../modals/ok-modal/ok-modal.component';

/**
 * One toolbar strip above a full-width report: the report selector, the PDF
 * viewer's page/zoom controls (published by DocumentPreviewComponent via
 * DocumentViewerService — only present while a PDF preview is on screen), and
 * the Email/Download/Print/Upload actions. The viewer's own toolbar is hidden
 * so every action goes through these buttons and the report audit.
 */
@Component({
  selector: 'app-report-toolbar',
  templateUrl: './report-toolbar.component.html',
  styleUrl: './report-toolbar.component.scss',
  imports: [FormsModule, NgSelectModule, NgTemplateOutlet],
})
export class ReportToolbarComponent extends BaseComponent {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly modalService = inject(ModalService);
  private readonly emailService = inject(EmailService);
  private readonly reportAuditService = inject(ReportAuditService);
  private readonly notificationService = inject(NotificationService);
  private readonly fileService = inject(FileService);
  private readonly dragAndDropService = inject(DragAndDropService);
  private readonly documentPipelineService = inject(DocumentPipelineService);

  private readonly reportContent = viewChild<ElementRef<HTMLDivElement>>('reportContent');

  public readonly upload = input(false);
  public readonly folderName = input('');
  public readonly subFolderName = input('');
  public readonly container = input('');
  public readonly emailed = output();
  public readonly printed = output();
  public readonly downloaded = output();

  protected readonly viewer = inject(DocumentViewerService).viewer;

  private reports: ReportTemplate[] = [];
  protected reportNames: string[] = [];
  protected selectedReportName: string;
  protected selectedReport: ReportTemplate;

  private uploadDetails: DragAndDropDocument = {
    folderName: undefined,
    subFolderName: undefined,
    fileName: undefined,
    container: undefined,
  };

  setReports(reports: ReportTemplate[], selectFirst = false) {
    this.reports = reports;
    this.reportNames = reports.map((x) => x.report);
    if (selectFirst && reports.length > 0) {
      this.selectedReportName = reports[0].report;
    }
    this.selectedReport = reports.find((x) => x.report === this.selectedReportName);
    if (!this.selectedReport) {
      this.selectedReportName = undefined;
    }
  }

  protected onReportChange() {
    this.selectedReport = this.reports.find((x) => x.report === this.selectedReportName);
  }

  /** Page input guard: ignore empty/out-of-range values while the user types. */
  protected setViewerPage(page: number) {
    this.viewer()?.setPage(page);
  }

  protected onEmail() {
    this.email(this.selectedReport.suffix, this.selectedReport.sellingCompany, this.selectedReport.report);
  }

  protected onDownload() {
    this.download(this.selectedReport.suffix);
  }

  protected onUpload() {
    this.uploadToBlob(this.selectedReport.suffix);
  }

  private email(suffix?: string, sellingCompany?: string, emailSubject?: string) {
    const { modal, component } = this.modalService.openMedium<EmailModalComponent>(EmailModalComponent);
    component.reportTemplate.set({
      ...this.selectedReport,
      emailSubject: `${emailSubject}-${suffix}`,
      emailBody: this.selectedReport.emailBody ?? ' ',
      suffix: suffix,
    });
    modal.result.then(async (model) => {
      let pdf: FormData;
      try {
        pdf = await this.getReportPdfFormData(suffix);
      } catch (err: unknown) {
        this.notificationService.fail(
          await this.documentPipelineService.getErrorMessage(
            err,
            $localize`Failed to send ${this.selectedReportName} email`,
          ),
        );
        return;
      }
      let ccEmail = model.ccEmail ?? ' ';
      if (model.emailContacts && model.emailContacts.length > 0) {
        model.emailContacts.forEach((x) => (ccEmail += x.email + ','));
        ccEmail = ccEmail.substring(0, ccEmail.length - 1);
      }
      this.emailService
        .emailReportWithSellingCompany(
          model.email,
          ccEmail,
          model.emailSubject,
          model.emailBody,
          pdf,
          sellingCompany,
          model.attachDocuments ?? false,
          model.type ?? ' ',
          model.suffix,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.emailSuccess(this.selectedReportName);
            this.reportAuditService
              .updateReportAuditDetails(this.selectedReport.type, this.selectedReport.suffix, ReportStatuses.emailed)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => this.emailed.emit(),
                error: (error: HttpErrorResponse) => {
                  const { modal, component } = this.modalService.openMedium<OkModalComponent>(OkModalComponent);
                  component.header.set($localize`Create Contact`);
                  component.message.set(error.error.split('!').join('<br/>'));
                  modal.result.then(ignoreModalConfirm, ignoreModalError);
                },
              });
          },
          error: () => this.notificationService.emailFail(this.selectedReportName),
        });
    }, ignoreModalError);
  }

  private async download(suffix?: string) {
    if (this.useDocumentPipeline) {
      this.documentPipelineService
        .download(this.selectedReport.documentType, this.selectedReport.reportId, this.reportFileName(suffix))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (blob: Blob) => {
            this.documentPipelineService.saveBlob(blob, `${this.reportFileName(suffix)}.pdf`);
            this.audit(ReportStatuses.downloaded, () => this.downloaded.emit());
          },
          error: async (err: unknown) =>
            this.notificationService.fail(
              await this.documentPipelineService.getErrorMessage(
                err,
                $localize`Failed to download ${this.selectedReportName}`,
              ),
            ),
        });
      return;
    }

    this.audit(ReportStatuses.downloaded, () => this.downloaded.emit());
    await this.fileService.downloadPdf(this.reportContent().nativeElement, this.selectedReportName, suffix);
  }

  protected print() {
    if (this.useDocumentPipeline) {
      // Download (not Preview): the server marks documents as printed only on
      // Download/Print actions — Preview must NEVER trigger status writes.
      this.documentPipelineService
        .download(
          this.selectedReport.documentType,
          this.selectedReport.reportId,
          this.reportFileName(this.selectedReport.suffix),
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (blob: Blob) => {
            this.documentPipelineService.printBlob(blob);
            this.audit(ReportStatuses.printed, () => this.printed.emit());
          },
          error: async (err: unknown) =>
            this.notificationService.fail(
              await this.documentPipelineService.getErrorMessage(
                err,
                $localize`Failed to print ${this.selectedReportName}`,
              ),
            ),
        });
      return;
    }

    this.audit(ReportStatuses.printed, () => this.printed.emit());
    const title = document.title;
    window.print();
    this.document.title = title;
  }

  private async uploadToBlob(suffix?: string) {
    let pdf: FormData;
    try {
      pdf = await this.getReportPdfFormData(suffix);
    } catch (err: unknown) {
      this.notificationService.fail(
        await this.documentPipelineService.getErrorMessage(
          err,
          $localize`Failed to upload ${this.selectedReportName} file`,
        ),
      );
      return;
    }

    this.uploadDetails.folderName = this.folderName();
    this.uploadDetails.subFolderName = this.subFolderName();
    this.uploadDetails.fileName = `${this.selectedReportName}${suffix ? '-' : ''}${suffix}.pdf`;
    this.uploadDetails.container = this.container();

    this.dragAndDropService.uploadPDF(this.uploadDetails, pdf).subscribe({
      next: () => this.notificationService.uploadSuccess(this.selectedReportName),
      error: () => this.notificationService.uploadFail(this.selectedReportName),
    });
  }

  /**
   * True when the selected report has opted into the unified document pipeline
   * (server-rendered PDF). See ReportTemplate.documentType/reportId. When false,
   * the legacy client-side path (jsPDF / window.print of the on-screen HTML) is used.
   */
  private get useDocumentPipeline(): boolean {
    return !!(this.selectedReport?.documentType && this.selectedReport?.reportId);
  }

  private reportFileName(suffix?: string): string {
    return `${this.selectedReportName}${suffix ? '-' : ''}${suffix ?? ''}`;
  }

  /** PDF for the Email/Upload flows: the server-rendered document when piloted, otherwise jsPDF. */
  private async getReportPdfFormData(suffix?: string): Promise<FormData> {
    if (this.useDocumentPipeline) {
      const blob = await firstValueFrom(
        this.documentPipelineService.download(
          this.selectedReport.documentType,
          this.selectedReport.reportId,
          this.reportFileName(suffix),
        ),
      );
      return this.documentPipelineService.toFormData(blob, this.reportFileName(suffix));
    }
    return this.fileService.getPdfOutput(this.reportContent().nativeElement, this.selectedReportName, suffix);
  }

  private audit(status: string, done: () => void) {
    this.reportAuditService
      .updateReportAuditDetails(this.selectedReport.type, this.selectedReport.suffix, status)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => done());
  }
}
